import { Injectable } from '@angular/core'
import { Router } from '@angular/router'

import { Actions, Effect } from '@ngrx/effects'
import { Action, select, Store } from '@ngrx/store'

import { Observable, of } from 'rxjs'
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { toPayload } from '../../core/helpers/rx-pipes'

import { AuthService } from '../services/auth.service'

import * as auth from '../actions/auth.actions'
import * as user from '../actions/user'
import * as pages from '../../base/actions/pages.actions'
import * as preferences from '../../base/actions/preferences.actions'

import * as fromAuth from '../reducers'

import { CATALOG_ONLY, ThemeConfig } from '../../core/helpers/theme-config'
import { Authenticate } from '../models/auth';

const routeName = ThemeConfig.viewMode === CATALOG_ONLY
  ? '/' + ThemeConfig.get<string>('catalog', 'route-name')
  : '/'

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private router: Router,
    private store: Store<fromAuth.State>
  ) { }

  @Effect()
  login$: Observable<Action> =
    this.actions$.ofType<auth.Login>(auth.LOGIN)
      .pipe(
        toPayload<Authenticate>(),
        switchMap(authDetails =>
          this.authService.login(authDetails)
            .pipe(
              map(res => new auth.LoginSuccess(res)),
              catchError(error => of(new auth.LoginFailure(error)))
            )
        )
      )

  @Effect()
  loginSuccess: Observable<Action> =
    this.actions$.ofType<auth.LoginSuccess>(auth.LOGIN_SUCCESS)
      .pipe(
        switchMap(action => of(<Action>new user.Get(), new preferences.GetTenantPreferences()))
      )

  @Effect({ dispatch: false })
  getLoginDone$: Observable<any> =
    this.actions$.ofType<Action>(user.GET_SUCCESS, user.GET_FAILED)
      .pipe(
        withLatestFrom(
          this.store.pipe(select(fromAuth.getReturnUrl)),
          (action, returnUrl) => returnUrl
        ),
        tap(returnUrl => this.router.navigateByUrl(
          returnUrl || routeName
        ))
      )

  @Effect({ dispatch: false })
  loginRedirect$: Observable<any> =
    this.actions$.ofType<auth.LoginRedirect>(auth.LOGIN_REDIRECT)
      .pipe(tap(() => this.router.navigate(['/login'])))

  @Effect()
  logoutRedirect$: Observable<Action> =
    this.actions$.ofType<auth.Logout>(auth.LOGOUT)
      .pipe(map(() => new auth.LoginRedirect('/')))
}
